import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductToolbar = ({ 
  onAddProduct, 
  onImport, 
  onExport, 
  onSyncCatalog, 
  totalProducts, 
  filteredProducts,
  isLoading 
}) => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSyncCatalog = async () => {
    setIsSyncing(true);
    try {
      await onSyncCatalog();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleImport = (file) => {
    onImport(file);
    setShowImportModal(false);
  };

  return (
    <>
      <div className="bg-surface rounded-lg border border-border p-4 mb-6">
        <div className="flex items-center justify-between">
          {/* Left Section - Stats */}
          <div className="flex items-center space-x-6">
            <div>
              <span className="text-2xl font-body-medium text-text-primary">
                {filteredProducts.toLocaleString('es-ES')}
              </span>
              <span className="text-sm text-text-muted ml-2">
                {filteredProducts !== totalProducts && `de ${totalProducts.toLocaleString('es-ES')} `}
                productos
              </span>
            </div>
            
            {isLoading && (
              <div className="flex items-center space-x-2 text-sm text-text-muted">
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span>Cargando...</span>
              </div>
            )}
          </div>

          {/* Right Section - Actions */}
          <div className="flex items-center space-x-3">
            {/* Sync Button */}
            <Button
              variant="secondary"
              onClick={handleSyncCatalog}
              disabled={isSyncing}
              iconName={isSyncing ? "Loader2" : "RefreshCw"}
              iconSize={16}
              className={isSyncing ? "animate-spin" : ""}
            >
              {isSyncing ? 'Sincronizando...' : 'Sincronizar Catálogo'}
            </Button>

            {/* Import/Export Dropdown */}
            <div className="relative group">
              <Button
                variant="secondary"
                iconName="Download"
                iconSize={16}
              >
                Importar/Exportar
              </Button>
              
              <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-lg shadow-modal border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-micro z-dropdown">
                <div className="py-2">
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                  >
                    <Icon name="Upload" size={16} />
                    <span>Importar productos</span>
                  </button>
                  <button
                    onClick={onExport}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                  >
                    <Icon name="Download" size={16} />
                    <span>Exportar productos</span>
                  </button>
                  <div className="border-t border-border my-1"></div>
                  <button
                    onClick={() => onExport('template')}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                  >
                    <Icon name="FileText" size={16} />
                    <span>Descargar plantilla</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Add Product Button */}
            <Button
              variant="primary"
              onClick={onAddProduct}
              iconName="Plus"
              iconSize={16}
            >
              Agregar Producto
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center space-x-8 mt-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span className="text-sm text-text-muted">En stock: 1.234</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-warning rounded-full"></div>
            <span className="text-sm text-text-muted">Stock bajo: 45</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-error rounded-full"></div>
            <span className="text-sm text-text-muted">Sin stock: 12</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-secondary-400 rounded-full"></div>
            <span className="text-sm text-text-muted">Archivados: 89</span>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
          <div className="bg-surface rounded-lg shadow-modal border border-border p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-body-medium text-text-primary">
                Importar Productos
              </h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="p-1 hover:bg-secondary-100 rounded transition-micro"
              >
                <Icon name="X" size={20} className="text-text-muted" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-text-muted">
                Selecciona un archivo CSV o Excel con los datos de los productos. 
                Asegúrate de que el archivo siga el formato de la plantilla.
              </p>

              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Icon name="Upload" size={32} className="text-text-muted mx-auto mb-4" />
                <p className="text-sm text-text-muted mb-2">
                  Arrastra y suelta tu archivo aquí, o
                </p>
                <Button variant="secondary" size="sm">
                  Seleccionar archivo
                </Button>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-text-muted">Formatos soportados:</span>
                <span className="text-text-primary">.csv, .xlsx, .xls</span>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button variant="ghost" onClick={() => setShowImportModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" disabled>
                Importar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductToolbar;